import { useState } from "react";
import {
  UserPlus,
  FolderOpen,
  Bell,
  CalendarDays,
  BarChart3,
  FileText,
  Palette,
  Sparkles,
  Settings,
  ChevronDown,
  Download,
  Play,
  Monitor,
  Users,
  Receipt,
  Bot,
  Smartphone,
} from "lucide-react";

const STEPS = [
  {
    title: "Create Your Account",
    icon: Download,
    color: "#3B82F6",
    content: (
      <ul className="space-y-2 text-slate-600 text-sm">
        <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Download ClientOps</li>
        <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Create an account using your email and password</li>
        <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Verify your information if prompted</li>
        <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Sign in to access your dashboard</li>
      </ul>
    ),
  },
  {
    title: "Add Your First Client",
    icon: UserPlus,
    color: "#22C55E",
    content: (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">Navigate to: <span className="font-medium text-slate-900 bg-slate-100 px-2 py-0.5 rounded">Clients &rarr; Add Client</span></p>
        <p className="text-slate-500">Enter the following details:</p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Client Name</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Email Address <span className="text-slate-400">(optional)</span></li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Phone Number <span className="text-slate-400">(optional)</span></li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Service Fee</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Follow-Up Interval</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Category</li>
        </ul>
        <p className="text-slate-500">Save the client to begin tracking activity.</p>
      </div>
    ),
  },
  {
    title: "Organize Your Clients",
    icon: FolderOpen,
    color: "#6366F1",
    content: (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">Use Categories to organize clients by:</p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Business Type</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Project</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Service</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Status</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Custom Workflow</li>
        </ul>
        <p className="text-slate-500">Clients can also remain Uncategorized until you are ready to organize them.</p>
      </div>
    ),
  },
  {
    title: "Schedule Follow-Ups",
    icon: Bell,
    color: "#F59E0B",
    content: (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">ClientOps automatically helps you stay on top of client relationships.</p>
        <p className="text-slate-500">To schedule a follow-up:</p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />Open a client record</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />Select a reminder interval</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />Save changes</li>
        </ul>
        <p className="text-slate-500">The Follow-Up Queue will automatically prioritize clients requiring attention.</p>
      </div>
    ),
  },
  {
    title: "Use the Calendar",
    icon: CalendarDays,
    color: "#3B82F6",
    content: (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">Navigate to: <span className="font-medium text-slate-900 bg-slate-100 px-2 py-0.5 rounded">Calendar &rarr; Add Event</span></p>
        <p className="text-slate-500">Create:</p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Meetings</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Calls</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Consultations</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Follow-Ups</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Custom Events</li>
        </ul>
        <p className="text-slate-500">Enable notifications to receive reminders before scheduled events.</p>
      </div>
    ),
  },
  {
    title: "Track Revenue",
    icon: BarChart3,
    color: "#22C55E",
    content: (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">Open the Dashboard to view:</p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Total Revenue</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Pending Revenue</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Overdue Revenue</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Client Activity</li>
        </ul>
        <p className="text-slate-500">Revenue Radar highlights opportunities that may require follow-up.</p>
      </div>
    ),
  },
  {
    title: "Create an Invoice",
    icon: FileText,
    color: "#F59E0B",
    content: (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">Navigate to: <span className="font-medium text-slate-900 bg-slate-100 px-2 py-0.5 rounded">Financials &rarr; Invoices &rarr; Create Invoice</span></p>
        <p className="text-slate-500">You can:</p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />Select a client</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />Add line items</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />Set due dates</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />Generate a PDF</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 flex-shrink-0" />Send invoices directly through your device</li>
        </ul>
      </div>
    ),
  },
  {
    title: "Customize Your Invoice Template",
    icon: Palette,
    color: "#6366F1",
    content: (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">Navigate to: <span className="font-medium text-slate-900 bg-slate-100 px-2 py-0.5 rounded">Profile &rarr; Invoice Template</span></p>
        <p className="text-slate-500">Customize:</p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Business Name</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Logo</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Brand Colors</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Payment Information</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 flex-shrink-0" />Terms &amp; Conditions</li>
        </ul>
        <p className="text-slate-500">Changes automatically apply to future invoices.</p>
      </div>
    ),
  },
  {
    title: "Use the AI Assistant",
    icon: Sparkles,
    color: "#3B82F6",
    content: (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">Navigate to: <span className="font-medium text-slate-900 bg-slate-100 px-2 py-0.5 rounded">AI Assistant</span></p>
        <p className="text-slate-500">The AI Assistant can help:</p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Draft follow-up emails</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Generate client communications</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Create invoice descriptions</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-shrink-0" />Provide business insights</li>
        </ul>
        <div className="mt-3 p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-lg">
          <p className="text-xs text-[#F59E0B] font-medium">Important: AI suggestions may be inaccurate. Always review content before sending or sharing with clients.</p>
        </div>
      </div>
    ),
  },
  {
    title: "Manage Your Account",
    icon: Settings,
    color: "#22C55E",
    content: (
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">Navigate to: <span className="font-medium text-slate-900 bg-slate-100 px-2 py-0.5 rounded">Profile</span></p>
        <p className="text-slate-500">Manage:</p>
        <ul className="space-y-2 text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Account Information</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Notifications</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Invoice Preferences</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Password Changes</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] mt-2 flex-shrink-0" />Account Deletion</li>
        </ul>
      </div>
    ),
  },
];

const StepAccordion = ({ step, index, isOpen, onToggle }) => (
  <div
    className={`bg-white rounded-2xl border transition-all ${
      isOpen ? "border-slate-300 shadow-md" : "border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300"
    }`}
    data-testid={`tutorial-step-${index}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-4 p-5 text-left"
      data-testid={`tutorial-toggle-${index}`}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${step.color}10` }}
      >
        <step.icon className="w-5 h-5" style={{ color: step.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-400">STEP {index + 1}</span>
        </div>
        <h3 className="font-semibold text-slate-900 text-sm sm:text-base" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {step.title}
        </h3>
      </div>
      <ChevronDown
        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
      />
    </button>
    {isOpen && (
      <div className="px-5 pb-5 pl-[4.75rem]">
        {step.content}
      </div>
    )}
  </div>
);

export default function GettingStartedSection() {
  const [openStep, setOpenStep] = useState(0);

  return (
    <section id="getting-started" className="py-24 bg-white" data-testid="getting-started-section">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2
            className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Getting Started with ClientOps
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            ClientOps is designed to help independent professionals manage clients, stay organized, track revenue, and streamline daily operations from one platform.
          </p>
        </div>

        <div className="space-y-3">
          {STEPS.map((step, i) => (
            <StepAccordion
              key={i}
              step={step}
              index={i}
              isOpen={openStep === i}
              onToggle={() => setOpenStep(openStep === i ? -1 : i)}
            />
          ))}
        </div>

        {/* Video Tutorials Placeholder */}
        <div className="mt-12 bg-[#F7F8FA] rounded-2xl border border-slate-200 p-8 text-center" data-testid="video-tutorials-placeholder">
          <div className="w-12 h-12 bg-[#3B82F6]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Play className="w-6 h-6 text-[#3B82F6]" />
          </div>
          <h3
            className="text-lg font-semibold text-slate-900 mb-2"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Video Tutorials
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Video tutorials coming soon.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              "Dashboard Overview",
              "Client Management",
              "Calendar Setup",
              "Invoicing Walkthrough",
              "AI Assistant Tutorial",
              "Subscription Management",
            ].map((topic, i) => (
              <span
                key={i}
                className="text-xs text-slate-400 bg-white border border-slate-200 px-3 py-1.5 rounded-full"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
